import { getMainSiteUrl } from "../constants/appConfig";

export function getAcf(page) {
  return page?.acf || {};
}

export function acfText(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

export function acfLocalizedText(acf, baseKey, lang = "ar") {
  const useEnglish = lang?.startsWith("en");
  const primaryKey = useEnglish ? `${baseKey}_en` : baseKey;
  const fallbackKey = useEnglish ? baseKey : `${baseKey}_en`;

  const primary = acfText(acf[primaryKey]);
  if (primary) return primary;

  return acfText(acf[fallbackKey]);
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getAcfNumberedIndices(acf, prefix, suffix = "") {
  const pattern = new RegExp(`^${escapeRegExp(prefix)}(\\d+)${escapeRegExp(suffix)}$`);
  const indices = new Set();

  Object.keys(acf).forEach((key) => {
    const match = key.match(pattern);
    if (match) {
      indices.add(Number.parseInt(match[1], 10));
    }
  });

  return [...indices].sort((a, b) => a - b);
}

export function acfList(acf, prefix) {
  return getAcfNumberedIndices(acf, prefix)
    .map((index) => acfText(acf[`${prefix}${index}`]))
    .filter(Boolean);
}

function collectAcfNumberedImages(acf, fieldPrefix) {
  return getAcfNumberedIndices(acf, fieldPrefix).map((id) => ({
    id,
    imageId: acf[`${fieldPrefix}${id}`],
  })).filter(({ imageId }) => typeof imageId === "number" && imageId > 0);
}

function collectAcfRepeaterImages(rows, imageKeys = ["logo", "image", "client_logo"]) {
  if (!Array.isArray(rows)) return [];

  return rows
    .map((row, index) => {
      if (!row || typeof row !== "object") return null;

      const imageId = imageKeys
        .map((key) => row[key])
        .find((value) => typeof value === "number" && value > 0);

      if (!imageId) return null;

      return { id: index + 1, imageId };
    })
    .filter(Boolean);
}

function isLikelyAcfMediaKey(key) {
  return (
    key === "hero_image" ||
    key.endsWith("_image") ||
    /^client_logo_\d+$/.test(key) ||
    /^project_image_\d+$/.test(key)
  );
}

function walkAcfMediaValues(value, add) {
  if (typeof value === "number") {
    add(value);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => walkAcfMediaValues(item, add));
    return;
  }

  if (value && typeof value === "object" && !value.url) {
    Object.values(value).forEach((item) => walkAcfMediaValues(item, add));
  }
}

export function resolveAcfLink(link) {
  const mainSite = getMainSiteUrl();

  if (!link) return null;

  if (typeof link === "string") {
    const trimmed = link.trim();
    if (!trimmed) return null;
    const href = trimmed.startsWith("/") ? `${mainSite}${trimmed}` : trimmed;
    return { href, label: trimmed, target: "_self" };
  }

  if (typeof link === "object" && link.url) {
    const url = link.url.trim();
    if (!url) return null;
    const href = url.startsWith("/") ? `${mainSite}${url}` : url;
    return {
      href,
      label: acfText(link.title) || href,
      target: link.target || "_self",
    };
  }

  return null;
}

export function collectLandingMediaIds(acf) {
  const ids = new Set();

  const add = (value) => {
    if (typeof value === "number" && value > 0) {
      ids.add(value);
    }
  };

  Object.entries(acf).forEach(([key, value]) => {
    if (key.startsWith("_")) return;
    if (!isLikelyAcfMediaKey(key)) return;
    walkAcfMediaValues(value, add);
  });

  collectAcfRepeaterImages(acf.client_logos).forEach(({ imageId }) => add(imageId));
  collectAcfRepeaterImages(acf.products, ["image", "product_image", "logo"]).forEach(
    ({ imageId }) => add(imageId),
  );
  collectAcfRepeaterImages(acf.projects, ["image", "project_image"]).forEach(({ imageId }) =>
    add(imageId),
  );

  return [...ids];
}

export function getLandingProducts(acf) {
  const fromNumberedFields = getAcfNumberedIndices(acf, "product_", "_title")
    .map((id) => {
      const title = acfText(acf[`product_${id}_title`]);
      if (!title) return null;

      return {
        id,
        title,
        description: acfText(acf[`product_${id}_description`]),
        imageId: acf[`product_${id}_image`],
        ctaText: acfText(acf[`product_${id}_cta_text`]),
        ctaLink: resolveAcfLink(acf[`product_${id}_cta_link`]),
      };
    })
    .filter(Boolean);

  if (fromNumberedFields.length > 0) {
    return fromNumberedFields;
  }

  if (!Array.isArray(acf.products)) {
    return [];
  }

  return acf.products
    .map((row, index) => {
      if (!row || typeof row !== "object") return null;

      const title = acfText(row.title || row.product_title);
      if (!title) return null;

      const imageId =
        row.image || row.product_image || row.product_image_id || row.product_1_image;

      return {
        id: index + 1,
        title,
        description: acfText(row.description || row.product_description),
        imageId: typeof imageId === "number" ? imageId : undefined,
        ctaText: acfText(row.cta_text || row.product_cta_text),
        ctaLink: resolveAcfLink(row.cta_link || row.product_cta_link),
      };
    })
    .filter(Boolean);
}

export function getLandingProductsSection(acf) {
  return {
    title: acfText(acf.products_title),
    description: acfText(acf.products_description),
    products: getLandingProducts(acf),
  };
}

export function getLandingProjects(acf) {
  const images = collectAcfNumberedImages(acf, "project_image_");

  if (images.length === 0 && Array.isArray(acf.project_gallery)) {
    collectAcfRepeaterImages(acf.project_gallery, ["image", "project_image"]).forEach(
      (item) => images.push(item),
    );
  }

  return {
    title: acfText(acf.projects_title),
    description: acfText(acf.projects_description),
    images,
  };
}

export function splitAcfParagraphs(value) {
  return acfText(value)
    .split(/\r?\n+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

export function getLandingClients(acf, { lang = "ar" } = {}) {
  const paragraphs = splitAcfParagraphs(
    acfLocalizedText(acf, "clients_description", lang),
  );
  const numberedLogos = collectAcfNumberedImages(acf, "client_logo_");
  const repeaterLogos = collectAcfRepeaterImages(acf.client_logos);
  const logos = numberedLogos.length > 0 ? numberedLogos : repeaterLogos;

  return {
    eyebrow: acfLocalizedText(acf, "clients_eyebrow", lang),
    title: acfLocalizedText(acf, "clients_title", lang),
    intro: paragraphs[0] || "",
    supportingText: paragraphs.slice(1).join(" ") || "",
    ctaText: acfLocalizedText(acf, "clients_cta_text", lang),
    ctaLink:
      resolveAcfLink(acf.clients_cta_link) ||
      resolveAcfLink(acf.hero_cta_link) ||
      resolveAcfLink(acf.quality_cta_link),
    logos,
  };
}
